
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import BackButton from "./BackButton";
import { fetchProductDetail } from "../api/productDetailApi";
import { fetchCompleteTheLook, formatCompleteTheLookData } from "../api/productDetailApi";
import { getUserProfile, getUsername } from "../utils/auth";
import { useCart } from "../context/CartContext";
import { userDetail } from "../HardcodeValues/userdetail";
import {
  isUsableProductDetail,
  toolResultToProductDetail,
} from "../utils/productDetailFromToolResult";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Stack,
  IconButton,
  Rating,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WishlistToggle from "./WishlistToggle";
import VirtualTryOn from "./VirtualTryOn";
import { trackProductViewed } from "../services/uiAgentSync";

// Styled Components
const ThumbnailImage = styled(Box)(({ theme, selected }) => ({
  width: 60,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  cursor: "pointer",
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "3px solid transparent",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    border: `3px solid ${theme.palette.grey[900]}`,
    transform: "scale(1.05)",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const MainImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: "hidden",
  backgroundColor: "#f5f5f5",
  height: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.95,
  },
}));

const MainImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  pointerEvents: "none", // Allow clicks to pass through to container
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  zIndex: 2,
}));

const SizeButton = styled(Button)(({ theme, selected }) => ({
  minWidth: 80,
  padding: "12px 20px",
  border: selected
    ? `2px solid ${theme.palette.grey[900]}`
    : `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: selected ? theme.palette.grey[900] : "white",
  color: selected ? "white" : theme.palette.grey[800],
  borderRadius: 4,
  fontSize: "0.875rem",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: selected ? theme.palette.grey[800] : theme.palette.grey[50],
    border: `2px solid ${theme.palette.grey[900]}`,
  },
}));

const ColorCircle = styled(Box)(({ theme, selected, colorvalue }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: colorvalue || theme.palette.grey[300],
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "2px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  position: "relative",
  "&:hover": {
    transform: "scale(1.1)",
    border: `3px solid ${theme.palette.grey[900]}`,
  },
  "&::after": selected
    ? {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "white",
        border: "1px solid rgba(0,0,0,0.3)",
      }
    : {},
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const QuantitySelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 0,
  padding: "8px 12px",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

// Extract color from variant (API may not have attributes.color - get from categories or title)
function normalizeColorLabel(color) {
  if (color == null || color === "") return null;
  if (Array.isArray(color)) {
    const first = color.find((c) => c != null && String(c).trim() !== "");
    return first != null ? String(first).trim() : null;
  }
  if (typeof color === "string") {
    const trimmed = color.trim();
    return trimmed || null;
  }
  const asString = String(color).trim();
  return asString || null;
}

function getVariantColor(variant) {
  if (variant?.attributes?.color) {
    const normalized = normalizeColorLabel(variant.attributes.color);
    if (normalized) return normalized;
  }
  const cat = variant?.categories?.[0];
  if (cat) {
    const parts = cat.split(" > ").map((p) => p.trim()).filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && !["Dark", "Light"].includes(last)) return last;
    if (parts.length >= 2) return parts[parts.length - 2];
  }
  const title = variant?.title || "";
  const match = title.match(/(?:Titti\s+)?(Dark\s+\w+|\w+)\s+(?:Jersey|Melange|Solid)/i) ||
    title.match(/(Black|Blue|Grey|Gray|Red|White|Navy|Brown|Beige|Green|Pink|Yellow)/i);
  return match ? match[1].trim() : null;
}

export default function HomeProductDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: productId } = useParams();
  const { cartItems, addToCart, updateQuantity } = useCart();

  const [productDetail, setProductDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI → Agent sync: last product id reported via product_viewed
  const trackedProductIdRef = useRef(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState(null); // null = main product
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewQuantity, setPreviewQuantity] = useState(1);

  // Complete the Look states
  const [completeTheLook, setCompleteTheLook] = useState(null);
  const [loadingCompleteTheLook, setLoadingCompleteTheLook] = useState(false);

  // Virtual Try-On state
  const [tryOnOpen, setTryOnOpen] = useState(false);

  const userProfile = getUserProfile();
  const customerId = userProfile?.customer_id;
  const username = getUsername();

  // Color mapping
  const getColorHex = (colorName) => {
    const label = normalizeColorLabel(colorName);
    if (!label) return "#808080";

    const colorMap = {
      black: "#000000",
      white: "#FFFFFF",
      "mesh black": "#1a1a1a",
      "off white": "#F5F5DC",
      brown: "#8B4513",
      beige: "#F5F5DC",
      blue: "#0000FF",
      red: "#FF0000",
      green: "#008000",
      yellow: "#FFFF00",
      pink: "#FFC0CB",
      gray: "#808080",
      grey: "#808080",
    };
    return colorMap[label.toLowerCase()] || "#808080";
  };

  // Collect all images from product and variants
  const allImages = useMemo(() => {
    const images = [];

    if (productDetail?.product?.images) {
      productDetail.product.images.forEach((img) => {
        images.push({
          url: img,
          id: productDetail.product.id,
          label: "Main",
        });
      });
    }

    if (productDetail?.variants) {
      productDetail.variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) => {
            images.push({
              url: img,
              id: variant.id,
              label: variant.attributes?.color || "Variant",
            });
          });
        }
      });
    }

    return images;
  }, [productDetail]);

  // Get colors from variants (with variant ref for selection)
  const colorListWithVariants = useMemo(() => {
    const list = [];
    const product = productDetail?.product;
    const variants = productDetail?.variants || [];

    // Add main product color
    const productColor = product && getVariantColor(product);
    if (productColor) {
      list.push({ color: productColor, variantId: null });
    }
    // Add variant colors (avoid duplicates)
    variants.forEach((variant) => {
      const color = getVariantColor(variant);
      if (color && !list.some((x) => x.color === color)) {
        list.push({ color, variantId: variant.id });
      }
    });
    // Fallback: attributes.color if getVariantColor didn't find any
    if (list.length === 0 && product?.attributes?.color) {
      const raw = product.attributes.color;
      const colors = Array.isArray(raw)
        ? raw.map(normalizeColorLabel).filter(Boolean)
        : String(raw)
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
      colors.forEach((c) => list.push({ color: c, variantId: null }));
    }
    return list;
  }, [productDetail]);

  const colorList = useMemo(
    () => colorListWithVariants.map((x) => x.color),
    [colorListWithVariants]
  );

  // Safely get current display image (handles out-of-bounds index when switching products)
  const currentDisplayImage = useMemo(() => {
    if (!allImages.length) return null;
    const idx = Math.min(currentImageIndex, allImages.length - 1);
    return allImages[idx] || null;
  }, [allImages, currentImageIndex]);

  // Display product: selected variant or main product
  const displayProduct = useMemo(() => {
    if (!productDetail?.product) return null;
    if (selectedVariantId && productDetail.variants?.length) {
      const variant = productDetail.variants.find((v) => v.id === selectedVariantId);
      if (variant) return variant;
    }
    return productDetail.product;
  }, [productDetail, selectedVariantId]);

  // UI → Agent sync: report the viewed product — fires on initial load and
  // again whenever the shopper switches variants (thumbnail or color click),
  // so the agent always knows which variant is on screen.
  useEffect(() => {
    if (!displayProduct?.id) return;
    const id = String(displayProduct.id);
    if (trackedProductIdRef.current === id) return;
    const isInPageSwitch = trackedProductIdRef.current !== null;
    trackedProductIdRef.current = id;
    trackProductViewed({
      productId: id,
      productTitle: displayProduct.title ?? displayProduct.name ?? null,
      fromView: isInPageSwitch ? "product_detail" : null,
    });
  }, [displayProduct]);

  // Find existing cart item matching id (variant or main), size, and color
  const displayProductId = displayProduct?.id || productId;
  const existingCartItem = useMemo(() => {
    if (!displayProductId || !selectedSize) return null;
    return cartItems.find(
      (item) =>
        item.id === displayProductId &&
        item.size === selectedSize &&
        item.color === selectedColor
    );
  }, [cartItems, displayProductId, selectedSize, selectedColor]);

  const isInCart = !!existingCartItem;
  const quantity = isInCart ? existingCartItem.quantity : previewQuantity;
  const isAlreadyInCart = isInCart;

  // Load Complete the Look recommendations
  const loadCompleteTheLook = async (articleId) => {
    if (!customerId) return;
    
    setLoadingCompleteTheLook(true);
    try {
      const data = await fetchCompleteTheLook(customerId, articleId);
      const formattedData = formatCompleteTheLookData(data);
      setCompleteTheLook(formattedData);
    } catch (err) {
      console.error("Failed to load Complete the Look:", err);
      setCompleteTheLook(null);
    } finally {
      setLoadingCompleteTheLook(false);
    }
  };

  const applyProductDetailData = useCallback(
    (data) => {
      setProductDetail(data);
      setSelectedVariantId(null);
      setCurrentImageIndex(0);

      const firstColor =
        getVariantColor(data?.product) ||
        getVariantColor(data?.variants?.[0]) ||
        normalizeColorLabel(data?.product?.attributes?.color);
      if (firstColor) setSelectedColor(firstColor);

      if (data?.product?.attributes?.size) {
        const sizes = Array.isArray(data.product.attributes.size)
          ? data.product.attributes.size
          : String(data.product.attributes.size)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);

        if (sizes.length > 0) {
          const productCategories = data?.product?.categories || [];
          const categoryString = Array.isArray(productCategories)
            ? productCategories.join(" ").toLowerCase()
            : String(productCategories).toLowerCase();
          const isFootwear =
            categoryString.includes("footwear") ||
            categoryString.includes("shoe") ||
            categoryString.includes("sandal") ||
            categoryString.includes("boot");

          let defaultSize = null;
          if (username && userDetail[username]) {
            const userSizes = userDetail[username];
            defaultSize = isFootwear
              ? userSizes["Footwear Size"]
              : userSizes["Size"];
          }

          if (defaultSize && sizes.includes(defaultSize)) {
            setSelectedSize(defaultSize);
          } else {
            setSelectedSize(sizes[0]);
          }
        }
      }

      loadCompleteTheLook(productId);
    },
    [productId, username]
  );

  const resolvePreloadedProductDetail = useCallback(() => {
    const navState = location.state;
    if (!navState || typeof navState !== "object") return null;

    if (isUsableProductDetail(navState.productDetail, productId)) {
      return navState.productDetail;
    }

    if (navState.product) {
      const fromProduct = toolResultToProductDetail(navState.product);
      if (isUsableProductDetail(fromProduct, productId)) {
        return fromProduct;
      }
    }

    return null;
  }, [location.state, productId]);

  useEffect(() => {
    if (!productId) {
      setError("Missing product ID");
      setLoading(false);
      return;
    }

    if (!customerId) {
      sessionStorage.setItem("pendingProductId", productId);
      window.dispatchEvent(
        new CustomEvent("openLoginModal", { detail: { productId } })
      );
      setError("Please sign in to view product details");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const preloaded = resolvePreloadedProductDetail();
        if (preloaded) {
          if (!cancelled) {
            applyProductDetailData(preloaded);
          }
          return;
        }

        const data = await fetchProductDetail(productId, customerId);
        if (!cancelled) {
          applyProductDetailData(data);
        }
      } catch (err) {
        console.error("Failed to fetch product detail:", err);
        if (!cancelled) {
          setError(err.message || "Failed to load product details");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [
    productId,
    customerId,
    location.key,
    applyProductDetailData,
    resolvePreloadedProductDetail,
  ]);

  const cleanImageUrl = (url) => {
    if (typeof url !== "string") return "";
    return url
      .trim()
      .replace(/storage\.cloud\.google\.com/g, "storage.googleapis.com");
  };

  const safeSeed = (input) => {
    return encodeURIComponent(String(input).replace(/\s+/g, "-").substring(0, 50));
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Please select a size before adding to cart.");
      return;
    }

    if (!productDetail?.product) {
      alert("Product data is missing.");
      return;
    }

    const product = productDetail.product;

    if (existingCartItem) {
      if (quantity !== existingCartItem.quantity) {
        updateQuantity(existingCartItem.id, quantity);
      }
      return;
    }

    addToCart({
      id: product?.id || productId,
      title: product.title || "Product",
      price: parseFloat(
        product.price_info?.price || product.price_info?.originalPrice || 0
      ),
      currency:
        product.price_info?.currencyCode ||
        product.price_info?.currency ||
        "USD",
      description: product.description || "",
      image: product.images?.[0] ? cleanImageUrl(product.images[0]) : "",
      size: selectedSize,
      color: selectedColor,
      variants: productDetail.variants || [],
      quantity: previewQuantity,
    });
  };

  // Handle thumbnail click: update image and variant selection
  const handleThumbnailClick = (index, image) => {
    setCurrentImageIndex(index);
    const product = productDetail?.product;
    const variants = productDetail?.variants || [];
    if (image?.id && product?.id !== image.id) {
      const variant = variants.find((v) => v.id === image.id);
      if (variant) {
        setSelectedVariantId(variant.id);
        const color = getVariantColor(variant);
        if (color) setSelectedColor(color);
      }
    } else {
      setSelectedVariantId(null);
      if (product) {
        const color = getVariantColor(product);
        if (color) setSelectedColor(color);
      }
    }
  };

  // Handle color selection: switch to that variant's data and image
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    const entry = colorListWithVariants.find((x) => x.color === color);
    if (entry) {
      setSelectedVariantId(entry.variantId);
      // Switch to that variant's image in the gallery
      if (entry.variantId) {
        const idx = allImages.findIndex((img) => img.id === entry.variantId);
        if (idx >= 0) setCurrentImageIndex(idx);
      } else {
        // Main product - switch to first product image
        const idx = allImages.findIndex((img) => img.id === productDetail?.product?.id);
        if (idx >= 0) setCurrentImageIndex(idx);
      }
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const incrementQuantity = () => {
    if (isInCart) {
      updateQuantity(existingCartItem.id, existingCartItem.quantity + 1);
    } else {
      setPreviewQuantity((prev) => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (isInCart) {
      if (existingCartItem.quantity > 1) {
        updateQuantity(existingCartItem.id, existingCartItem.quantity - 1);
      } else {
        updateQuantity(existingCartItem.id, 0);
      }
    } else if (previewQuantity > 1) {
      setPreviewQuantity((prev) => prev - 1);
    }
  };

  // Handle Complete the Look product click
  const handleCompleteTheLookClick = (recommendation) => {
    if (!recommendation?.id) return;
    navigate(`/item/${recommendation.id}`);
  };

  // Handle size change
  const handleSizeChange = (newSize) => {
    setSelectedSize(newSize);
    
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackButton />
        <Box sx={{ textAlign: "center", py: 10 }}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            Loading product details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || (!productDetail?.product && !loading)) {
    const isAuthError = error === "Please sign in to view product details";

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackButton />
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            {error || "Product data not available"}
          </Typography>
          {isAuthError && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              The login modal should open automatically. If it doesn't, please
              click the sign in button in the navigation bar.
            </Typography>
          )}
          <Button onClick={() => navigate(-1)} variant="contained" sx={{ mt: 4 }}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const product = displayProduct || productDetail?.product;
  const originalPrice = parseFloat(product?.price_info?.originalPrice) || 0;
  const displayPrice = parseFloat(product?.price_info?.price) || originalPrice;
  const isOnSale = originalPrice > displayPrice && displayPrice > 0;

  const sizeList = product?.attributes?.size
    ? Array.isArray(product?.attributes?.size)
      ? product.attributes.size
      : String(product?.attributes?.size)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
    : [];

  const rating = parseFloat(product.attributes?.rating) || 4.9;
  const reviewCount = parseInt(product.attributes?.review_count) || 89;

  return ( <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackButton />

      <Grid container spacing={6} sx={{ mb: 8 }}>
        {/* Image Gallery */}
       <Grid item xs={12} md={6}>
  <Box sx={{ display: "flex", gap: 2 }}>
    {/* Thumbnails Column - Made Scrollable */}
    <Box 
      sx={{ 
        width: 80, 
        overflowY: 'auto', 
        maxHeight: 'calc(100vh - 200px)', // Adjust this value based on your layout needs
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.2) rgba(0,0,0,0.1)',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.3)',
        }
      }}
    >
      <Stack spacing={1.5}>
        {allImages.map((image, index) => (
          <ThumbnailImage
            key={index}
            selected={currentImageIndex === index}
            onClick={() => handleThumbnailClick(index, image)}
          >
            <img
              src={cleanImageUrl(image.url)}
              alt={`Thumbnail ${index + 1}`}
              onError={(e) => {
                e.target.src = `https://picsum.photos/seed/thumb${index}/100/120`;
              }}
            />
          </ThumbnailImage>
        ))}
      </Stack>
    </Box>

    {/* Main Image */}
    <Box sx={{ flex: 1 }}>
      <MainImageContainer
        onClick={() => {
          
          setTryOnOpen(true);
        }}
        sx={{ cursor: "pointer" }}
      >
        <MainImage
          src={currentDisplayImage ? cleanImageUrl(currentDisplayImage.url) : ""}
          alt={product.title}
          onError={(e) => {
            const seed = (productId || "fallback").replace(/\s+/g, "");
            e.target.src = `https://picsum.photos/seed/${seed}/800/600`;
          }}
        />

        {/* Navigation Arrows */}
        {allImages.length > 1 && (
          <>
            <NavButton 
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }} 
              sx={{ left: 16 }}
            >
              <ChevronLeftIcon />
            </NavButton>
            <NavButton 
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }} 
              sx={{ right: 16 }}
            >
              <ChevronRightIcon />
            </NavButton>
          </>
        )}

        {/* Badges */}
        <Box 
          sx={{ position: "absolute", top: 16, left: 16 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Chip
            icon={<Box component="span" sx={{ fontSize: "16px" }}>⭐</Box>}
            label="Highly Rated"
            size="small"
            sx={{
              bgcolor: "white",
              fontWeight: 500,
              fontSize: "0.75rem",
              boxShadow: 1,
              height: "28px",
              "& .MuiChip-icon": {
                marginLeft: "8px",
                marginRight: "-4px",
              },
            }}
          />
        </Box>
        <Box 
          sx={{ position: "absolute", top: 16, right: 16 }}
          onClick={(e) => {
            e.stopPropagation();
            setTryOnOpen(true);
          }}
        >
          <Chip
            icon={<Box component="span" sx={{ fontSize: "16px" }}>📷</Box>}
            label="Try-On"
            size="small"
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              fontWeight: 500,
              fontSize: "0.75rem",
              boxShadow: 1,
              height: "28px",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.9)",
              },
              "& .MuiChip-icon": {
                marginLeft: "8px",
                marginRight: "-4px",
                color: "white",
              },
            }}
          />
        </Box>
      </MainImageContainer>
    </Box>
  </Box>
</Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Title & Rating */}
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
                {product.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Rating value={rating} readOnly precision={0.1} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {rating} ({reviewCount} reviews)
                </Typography>
              </Box>

              {/* Price */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                {isOnSale ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography
                        sx={{
                          fontSize: "1.75rem",
                          fontWeight: 700,
                          lineHeight: 1,
                          color: "#D32F2F",
                          letterSpacing: "-0.3px",
                        }}
                      >
                        ${Math.floor(displayPrice)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          marginLeft: "1px",
                          color: "#D32F2F",
                          lineHeight: 1,
                        }}
                      >
                        {displayPrice.toFixed(2).slice(displayPrice.toFixed(2).indexOf("."))}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#9E9E9E",
                        textDecoration: "line-through",
                      }}
                    >
                      ${originalPrice.toFixed(2)}
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "baseline" }}>
                    <Typography
                      sx={{
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        color: "#000",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      ${Math.floor(displayPrice)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        marginLeft: "1px",
                        color: "#000",
                        lineHeight: 1,
                      }}
                    >
                      {displayPrice.toFixed(2).slice(displayPrice.toFixed(2).indexOf("."))}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography variant="body1" color="text.secondary" paragraph>
                {product.description || "No description available."}
              </Typography>
            </Box>

            {/* Color Selector */}
            {colorList.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Color{" "}
                  <Typography component="span" color="text.secondary" fontWeight={400}>
                    {selectedColor}
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  {colorList.map((color, index) => (
                    <ColorCircle
                      key={index}
                      selected={selectedColor === color}
                      colorvalue={getColorHex(color)}
                      onClick={() => handleColorSelect(color)}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Size Selector */}
            {sizeList.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Size
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {sizeList.map((size, index) => (
                    <SizeButton
                      key={index}
                      selected={selectedSize === size}
                      onClick={() => handleSizeChange(size)}
                    >
                      {size}
                    </SizeButton>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Size Review / Size Fit Note */}
            {(product?.attributes?.size_fit_note ||
              product?.size_fit_note ||
              productDetail?.product?.size_fit_note ||
              productDetail?.size_fit_note) && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Size Review
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                  }}
                >
                  {product?.attributes?.size_fit_note ||
                    product?.size_fit_note ||
                    productDetail?.product?.size_fit_note ||
                    productDetail?.size_fit_note}
                </Typography>
              </Box>
            )}

            {/* Quantity */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Quantity
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <QuantitySelector>
                  <QuantityButton onClick={decrementQuantity} disabled={quantity <= 1}>
                    <RemoveIcon fontSize="small" />
                  </QuantityButton>
                  <Typography
                    sx={{
                      px: 3,
                      fontWeight: 600,
                      minWidth: 40,
                      textAlign: "center",
                    }}
                  >
                    {quantity}
                  </Typography>
                  <QuantityButton onClick={incrementQuantity}>
                    <AddIcon fontSize="small" />
                  </QuantityButton>
                </QuantitySelector>
                <Typography variant="body2" color="text.secondary">
                  Only {Math.min(9, Math.floor(Math.random() * 10) + 1)} items left in stock
                </Typography>
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={handleAddToCart}
                disabled={!selectedSize}
                variant="contained"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  textTransform: "none",
                  bgcolor: "grey.900",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "grey.800" },
                  ...(isAlreadyInCart && {
                    bgcolor: "success.main",
                    "&:hover": { bgcolor: "success.dark" },
                  }),
                }}
              >
                {isAlreadyInCart
                  ? "✓ Added to Cart"
                  : selectedSize
                  ? "Add to Cart"
                  : "Select a Size"}
              </Button>
              <WishlistToggle product={product} />
              <IconButton sx={{ border: "1px solid #e0e0e0" }}>
                <ShareIcon />
              </IconButton>
            </Stack>

            {/* Features */}
            <Stack direction="row" spacing={3} sx={{ pt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                <Typography variant="body2">Free Shipping</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityIcon fontSize="small" />
                <Typography variant="body2">Secure Payment</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RefreshIcon fontSize="small" />
                <Typography variant="body2">Easy Returns</Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Product Details */}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.id || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.categories?.[0]?.split(" > ").pop() || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Material
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.attributes?.fabric || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fit
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.attributes?.fit || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Complete the Look Section */}
      {completeTheLook && completeTheLook.recommendations && completeTheLook.recommendations.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Complete the Look
          </Typography>

          {loadingCompleteTheLook ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {completeTheLook.recommendations.map((recommendation, index) => {
                const itemImage = cleanImageUrl(recommendation.image) ||
                  `https://picsum.photos/seed/${safeSeed(recommendation.id)}/400/300`;

                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <ProductCard onClick={() => handleCompleteTheLookClick(recommendation)}>
                      {/* Style Match Badge */}
                      {recommendation.styleMatch && (
                        <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}>
                          <Chip
                            label="Style Match"
                            size="small"
                            sx={{
                              bgcolor: "#4CAF50",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      )}

                      <CardMedia
                        component="img"
                        height="200"
                        image={itemImage}
                        alt={recommendation.name || "Product"}
                        sx={{ objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = `https://picsum.photos/seed/${safeSeed(recommendation.id)}/400/300`;
                        }}
                      />
                      <CardContent>
                       <Box
                         sx={{
                           display: "flex",
                           justifyContent: "space-between",
                           alignItems: "flex-start", // allows title to wrap while heart stays top-aligned
                           mb: 1,
                         }}
                       >
                         <Typography
                           variant="body2"
                           sx={{
                             fontWeight: 500,
                             color: "text.primary",
                             flex: 1,
                             mr: 1,
                             overflow: "hidden",
                             textOverflow: "ellipsis",
                             display: "-webkit-box",
                             WebkitLineClamp: 2,
                             WebkitBoxOrient: "vertical",
                             "&:hover": {
                               color: "primary.main",
                             },
                           }}
                         >
                           {recommendation.name}
                         </Typography>
                         {/* ✅ Wishlist Toggle — inline, right-aligned */}
                         <Box sx={{ flexShrink: 0, mt: '-2px' }}>
                           <WishlistToggle product={recommendation} />
                         </Box>
                       </Box>
                        {/* Product Type */}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {recommendation.productType}
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="h6" fontWeight={700}>
                            ${recommendation.price.toFixed(2)}
                          </Typography>

                          {/* Personalization Score */}
                          {recommendation.personalizedBoost > 0 && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Rating
                                value={5}
                                max={5}
                                readOnly
                                size="small"
                                sx={{ fontSize: "0.9rem" }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {recommendation.score.toFixed(1)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </ProductCard>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Virtual Try-On Modal */}
      <VirtualTryOn
        open={tryOnOpen}
        onClose={() => setTryOnOpen(false)}
        productImage={
          currentDisplayImage ? cleanImageUrl(currentDisplayImage.url) : ""
        }
        article_id={productId}
      />
    </Container>);
}